import { proxy } from 'valtio';
import { EditorMode } from '@duke-hq/shared-constants/assessments';

import type { UnitAssessmentSlides } from './types';
import type { IncompleteSlide } from '@duke-hq/svc-api/lib/assessment/incompleteSlides';
import { SlideKind } from '@duke-hq/svc-api-db';

const EditorType = {
  ...SlideKind,
  // Not technically slide types, but we need to be able to edit them in similar way
  FORM_SECTION: 'FORM_SECTION',
  FORM_FIELD: 'FORM_FIELD',
  MULTI_CHOICE_QUESTION: 'MULTI_CHOICE_QUESTION',
} as const;

type EditorType = (typeof EditorType)[keyof typeof EditorType];

type UnitAssessmentState = {
  isEditorOpen: boolean;
  currentSlide: UnitAssessmentSlides[0] | null;
  editorMode: EditorMode | '';
  editorType: EditorType | '';
  slidesLength: number;
  currentAssessmentId: string;
  currentMultiChoiceQuestionIndex: number;
  newlyCreatedSlideId: string | null;
  currentFormSectionId: string | null;
  newlyCreatedFormSectionId: string | null;
  currentFormFieldId: string | null;
  draftVersionId: string | null;
  hasUnpublishedChanges: boolean;
  incompleteSlides: IncompleteSlide[];

  openEditor: () => void;
  closeEditor: () => void;
  setCurrentSlide: (slide: UnitAssessmentSlides[0] | null) => void;
  setEditorMode: (mode: EditorMode) => void;
  setEditorType: (type: EditorType) => void;
  setSlidesLength: (length: number) => void;
  setCurrentAssessmentId: (id: string) => void;
  setCurrentMultiChoiceQuestionIndex: (index: number) => void;
  setNewlyCreatedSlideId: (id: string | null) => void;
  setCurrentFormSectionId: (id: string | null) => void;
  setNewlyCreatedFormSectionId: (id: string | null) => void;
  setCurrentFormFieldId: (id: string | null) => void;
  setDraftVersionId: (id: string | null) => void;
  setHasUnpublishedChanges: (value: boolean) => void;
  setIncompleteSlides: (slides: IncompleteSlide[]) => void;
};

export const unitAssessmentState = proxy<UnitAssessmentState>({
  isEditorOpen: false,
  currentSlide: null,
  editorMode: '',
  editorType: '',
  slidesLength: 0,
  currentAssessmentId: '',
  currentMultiChoiceQuestionIndex: 0,
  newlyCreatedSlideId: null,
  currentFormSectionId: null,
  newlyCreatedFormSectionId: null,
  currentFormFieldId: null,
  draftVersionId: null,
  hasUnpublishedChanges: false,
  incompleteSlides: [],
  openEditor: () => {
    unitAssessmentState.isEditorOpen = true;
  },
  closeEditor: () => {
    unitAssessmentState.isEditorOpen = false;
    unitAssessmentState.currentSlide = null;
    unitAssessmentState.editorMode = '';
    unitAssessmentState.editorType = '';
    unitAssessmentState.currentMultiChoiceQuestionIndex = 0;
    unitAssessmentState.currentAssessmentId = '';
    unitAssessmentState.slidesLength = 0;
  },
  setCurrentSlide: (slide: UnitAssessmentSlides[0] | null) => {
    unitAssessmentState.currentSlide = slide;
  },
  setEditorMode: (mode: EditorMode) => {
    unitAssessmentState.editorMode = mode;
  },
  setEditorType: (type: EditorType) => {
    unitAssessmentState.editorType = type;
  },
  setSlidesLength: (length: number) => {
    unitAssessmentState.slidesLength = length;
  },
  setCurrentAssessmentId: (id: string) => {
    unitAssessmentState.currentAssessmentId = id;
  },
  setCurrentMultiChoiceQuestionIndex: (index: number) => {
    unitAssessmentState.currentMultiChoiceQuestionIndex = index;
  },
  setNewlyCreatedSlideId: (id: string | null) => {
    unitAssessmentState.newlyCreatedSlideId = id;
  },
  setCurrentFormSectionId: (id: string | null) => {
    unitAssessmentState.currentFormSectionId = id;
  },
  setNewlyCreatedFormSectionId: (id: string | null) => {
    unitAssessmentState.newlyCreatedFormSectionId = id;
  },
  setCurrentFormFieldId: (id: string | null) => {
    unitAssessmentState.currentFormFieldId = id;
  },
  setDraftVersionId: (id: string | null) => {
    unitAssessmentState.draftVersionId = id;
  },
  setHasUnpublishedChanges: (value: boolean) => {
    unitAssessmentState.hasUnpublishedChanges = value;
  },
  setIncompleteSlides: (slides: IncompleteSlide[]) => {
    unitAssessmentState.incompleteSlides = slides;
  },
});
